import PropTypes from "prop-types";
import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import { Grid, TextField, MenuItem, Box, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "connected-react-router";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  updateCoursePrereqs,
  fetchCoursePrereqs,
  fetchCourses
} from "../../../../redux/actions/contentCoursesActions";
import DualList from "../../../../components/DualList/DualList";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { buildPolicyDetailsSettingsRoute } from "../../../../routes/AdminRoutes";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import useLoadingState from "../../../../hooks/useLoadingState";
import { enterKeyCode } from "../../../../constants";
import { useSnackbarNotifications } from "../../../../hooks/snackbar";

const trainingTypeFilters = [
  { value: "all", label: "courseTypes.all" },
  { value: "online", label: "courseTypes.online" },
  { value: "ilt", label: "courseTypes.ilt" },
  { value: "policy", label: "courseTypes.policy" }
];

const useStyles = makeStyles(theme => ({
  label: {
    marginRight: theme.spacing(3),
    display: "inline-block",
    color: theme.palette.text.secondary
  },
  selectType: {
    fontSize: 20,
    fontWeight: "600",
    color: theme.palette.primary.main,
    "&:before": {
      content: "none"
    },
    "&:after": {
      content: "none"
    }
  },
  selectTypeSelect: {
    paddingRight: theme.spacing(5)
  },
  secondaryText: {
    color: theme.palette.text.secondary
  },
  filterButton: {
    marginLeft: theme.spacing(3)
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.secondary[300]}`
  }
}));

// TODO: After redux for course data is refactored, update implementation for handling and/or equivalences for course prerequisites
const SetPrerequisites = ({
  fetchCoursePrereqs,
  updateCoursePrereqs,
  push,
  match: {
    params: { courseId }
  }
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUpdatingPrereqs = useLoadingState("UPDATE_COURSE_PREREQS");
  const [search, setSearch] = useState("");
  const [trainingType, setTrainingType] = useState(trainingTypeFilters[0]);
  const [available, setAvailable] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const {
    displaySuccessMessage,
    displayErrorMessage
  } = useSnackbarNotifications();

  const prereqs = useSelector(
    state => state.contentCoursesState && state.contentCoursesState.prereqs
  );

  const courseName = useSelector(
    state => state.contentCoursesState && state.contentCoursesState.courseName
  );

  useEffect(() => {
    fetchCoursePrereqs(courseId);
  }, []);

  useEffect(() => {
    if (!courseName) {
      dispatch(fetchCourses());
    }
  }, [courseName]);

  useEffect(() => {
    const availableCourses = [];
    const selectedCourses = [];
    if (prereqs) {
      prereqs.forEach(prereq => {
        prereq.subtitles = [prereq.type];
        if (prereq.isPrereq) {
          selectedCourses.push(prereq);
        } else {
          availableCourses.push(prereq);
        }
      });
      setAvailable(availableCourses);
      setSelected(selectedCourses);
    }
  }, [prereqs]);

  const clearAll = () => {
    setSearch("");
    setTrainingType(trainingTypeFilters[0]);
    setAppliedFilters({});
  };

  const filterBySearch = course => {
    if (!search) return true;
    let searchValue = search.toLowerCase().trim();
    let str = `${course.name} ${course.description} ${course.tags.join(
      " "
    )} ${course.trainingPackagesNames.join(" ")} ${
      course.number ? course.number : ""
    }`;
    return str.toLowerCase().indexOf(searchValue) >= 0;
  };

  const filterByType = course => {
    if (trainingType.value === "all") return true;
    return course.type.toLowerCase() === trainingType.value;
  };

  const handleFilter = () => {
    setAppliedFilters({ trainingType, available });
  };

  useEffect(() => {
    if (prereqs) {
      const filteredAvailable = prereqs.filter(
        course =>
          !course.isPrereq &&
          filterBySearch(course) &&
          filterByType(course) &&
          !selected.map(course => course.id).includes(course.id)
      );
      setAvailable(filteredAvailable);
    }
  }, [appliedFilters, prereqs]);

  const addAll = () => {
    let availableCoursesToAdd = available.map(course => {
      return { ...course, active: true, equivalenceType: "and" };
    });
    setSelected([...selected, ...availableCoursesToAdd]);
    setAvailable([]);
  };

  const removeAll = () => {
    let selectedCoursesToRemove = selected.map(course => {
      return { ...course, active: false };
    });
    setAvailable([...available, ...selectedCoursesToRemove]);
    setSelected([]);
  };

  /**
   * And/Or prereq equivalency is determined by the equivalenceType of the prereq that
   * comes before it. When we add a new prereq, we update the equivalenceType of the prereq
   * before it to be the default value "and". This should probably be updated/improved once the structure
   * of the course data is determined.
   */
  const addToSelectedCourses = selectedItem => {
    setAvailable(
      available.filter(availableItem => availableItem.id !== selectedItem.id)
    );
    let currentSelectedItems = selected;
    if (currentSelectedItems.length > 0) {
      currentSelectedItems[currentSelectedItems.length - 1].equivalenceType =
        "and";
    }
    setSelected([
      ...currentSelectedItems,
      {
        ...selectedItem,
        active: true,
        order: selected.length
      }
    ]);
  };

  const addToAvailableCourses = availableItem => {
    setSelected(
      selected.filter(selectedItem => selectedItem.id !== availableItem.id)
    );
    setAvailable([...available, { ...availableItem, active: false }]);
  };

  const handleSavePrereqs = () => {
    const prerequisiteIds = prereqs
      .filter(course => course.isPrereq)
      .map(course => course.id);
    const selectedIds = selected.map(course => course.id);
    const idsToRemove = prerequisiteIds.filter(id => !selectedIds.includes(id));
    const idsToAdd = selectedIds.filter(id => !prerequisiteIds.includes(id));
    updateCoursePrereqs(
      courseId,
      {
        idsToAdd: idsToAdd,
        idsToRemove: idsToRemove
      },
      () => displaySuccessMessage(t("setPrerequisites.successMsg")),
      () => displayErrorMessage(t("setPrerequisites.errorMsg"))
    );
  };

  const handleGoBack = () => {
    push(buildPolicyDetailsSettingsRoute(courseId));
  };

  const prereqsLists = useMemo(
    () => (
      <DualList
        renderList={(items, DualListItem, listRef) => (
          <Box flex={1} minHeight={460} mt={3}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  className="List"
                  height={height}
                  itemCount={items.length}
                  itemSize={69}
                  width={width}
                  ref={listRef}
                >
                  {({ index, style }) => {
                    return (
                      <div style={style}>{DualListItem(items[index])}</div>
                    );
                  }}
                </List>
              )}
            </AutoSizer>
          </Box>
        )}
        unselectedList={available}
        selectedList={selected}
        selectedListHeader={t("setPrerequisites.selectedCourses")}
        unselectedListHeader={t("setPrerequisites.availableCourses")}
        onAddAll={addAll}
        onRemoveAll={removeAll}
        onAddToSelectedList={addToSelectedCourses}
        onAddToUnselectedList={addToAvailableCourses}
      />
    ),
    [available, selected]
  );

  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={["Policies", courseName, t("setPrerequisites.pageTitle")]}
        />
      }
      onGoBackClick={handleGoBack}
    >
      <Box mt={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              value={search}
              onChange={ev => {
                setSearch(ev.target.value);
              }}
              InputProps={{
                onKeyDown: ({ keyCode }) =>
                  keyCode === enterKeyCode && handleFilter(),
                endAdornment: <SearchIcon className={classes.secondaryText} />,
                placeholder: t("setPrerequisites.searchFilterPlaceholder"),
                disableUnderline: true
              }}
              fullWidth
              variant="filled"
              hiddenLabel
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              value={trainingType}
              onChange={ev => {
                setTrainingType(ev.target.value);
              }}
              InputProps={{
                disableUnderline: true
              }}
              SelectProps={{
                renderValue: val =>
                  t("setPrerequisites.trainingTypeFilterLabel", {
                    type: t(val.label)
                  })
              }}
              fullWidth
              variant="filled"
              hiddenLabel
            >
              {trainingTypeFilters.map(type => (
                <MenuItem key={type.value} value={type}>
                  {t(type.label)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Box display="flex" alignItems="center" height="100%">
              <Button color="primary" onClick={clearAll}>
                {t("setPrerequisites.clearAllFiltersBtn")}
              </Button>
              <Button
                className={classes.filterButton}
                color="primary"
                variant="contained"
                onClick={handleFilter}
              >
                {t("setPrerequisites.filterListBtn")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <div className={classes.divider} />
      {prereqsLists}
      <Box display="flex" justifyContent="flex-end" mt={7.5}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSavePrereqs}
          disabled={isUpdatingPrereqs}
        >
          {t("setPrerequisites.savePrereqsBtn")}
        </Button>
      </Box>
    </AdminPageLayout>
  );
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchCoursePrereqs,
      updateCoursePrereqs,
      push
    },
    dispatch
  );
}

export default connect(null, mapDispatchToProps)(SetPrerequisites);

SetPrerequisites.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  updateCoursePrereqs: PropTypes.func,
  fetchCoursePrereqs: PropTypes.func
};
